// KPI içe aktarma sihirbazı — varsayılan yıl / işletme kararları (kpi-import-wizard.ts'ten ayrıldı,
// güvenlik/UX taraması 2026-10-08, 600 satır sınırı; davranış birebir, ana modül re-export eder).

import {
  notesSheetIndexes,
  type ClientEntity,
  type KpiImportColumn,
  type KpiImportGrid,
  type KpiMappingDefaults,
} from "@mahir/shared";

// ─── Varsayılan yıl / işletme (B-02/B-03) ───────────────────────────────────

/** Yıl kolonu olmayan ama değer kolonu (işletme) taşıyan sayfalar (grid dizi indeksi) — atlananlar hariç. */
export function sheetsNeedingYear(
  columns: readonly (readonly KpiImportColumn[])[],
  skippedSheets: readonly number[] = [],
): number[] {
  return columns
    .map((sheet, i) =>
      !skippedSheets.includes(i) &&
      sheet.some((c) => c.kind === "entity") &&
      !sheet.some((c) => c.kind === "year" && c.year !== undefined)
        ? i
        : -1,
    )
    .filter((i) => i >= 0);
}

/** Yıl kolonu olmayan ama değer kolonu (işletme) taşıyan sayfa var → "Yıl" seçimi zorunlu. */
export function needsDefaultYear(
  columns: readonly (readonly KpiImportColumn[])[],
  skippedSheets: readonly number[] = [],
): boolean {
  return sheetsNeedingYear(columns, skippedSheets).length > 0;
}

/** P2-6 — sayfanın etkin yılı: sayfa bazlı seçim, yoksa global seçim. */
export function effectiveSheetYear(
  defaults: KpiMappingDefaults,
  sheetIdx: number,
): number | undefined {
  return defaults.defaultYearBySheet?.[String(sheetIdx)] ?? defaults.defaultYear;
}

/** P2-6 — sayfa bazlı yıl yazımı (undefined → siler); diğer sayfalar korunur. */
export function setSheetYear(
  defaults: KpiMappingDefaults,
  sheetIdx: number,
  year: number | undefined,
): KpiMappingDefaults {
  const next: Record<string, number> = { ...(defaults.defaultYearBySheet ?? {}) };
  if (year === undefined) delete next[String(sheetIdx)];
  else next[String(sheetIdx)] = year;
  const out: KpiMappingDefaults = { ...defaults };
  if (Object.keys(next).length === 0) delete out.defaultYearBySheet;
  else out.defaultYearBySheet = next;
  return out;
}

/** P2-8 — sayfa atlama anahtarı; liste sıralı ve tekil. */
export function toggleSkippedSheet(
  defaults: KpiMappingDefaults,
  sheetIdx: number,
  skipped: boolean,
): KpiMappingDefaults {
  const set = new Set(defaults.skippedSheets ?? []);
  if (skipped) set.add(sheetIdx);
  else set.delete(sheetIdx);
  const out: KpiMappingDefaults = { ...defaults };
  if (set.size === 0) delete out.skippedSheets;
  else out.skippedSheets = [...set].sort((a, b) => a - b);
  return out;
}

/** P2-8 — çıkarımın "bilanço benzeri" bulduğu, henüz atlanmamış sayfalar (öneri satırı). */
export function suggestedSkipSheets(
  grid: KpiImportGrid,
  skippedSheets: readonly number[] = [],
): number[] {
  return grid.sheets
    .map((s, i) => (s.suggestedSkip && !skippedSheets.includes(i) ? i : -1))
    .filter((i) => i >= 0);
}

/** Seçilebilir işletmeler: aktif olanlar (portalda grid'in taşıdığı işletmeler). */
export function selectableEntities(entities: readonly ClientEntity[]): ClientEntity[] {
  return entities.filter((e) => e.isActive);
}

/**
 * Yıl kolonu olup işletme kolonu olmayan, atlanmamış sayfaların indeksleri. Varsayılan işletme
 * tanımlı değilse ve birden çok aktif işletme varsa "İşletme" seçimi zorunlu (grup müşteri —
 * sessiz "ilk işletme" yok); aksi halde boş döner.
 */
export function sheetsNeedingEntity(
  columns: readonly (readonly KpiImportColumn[])[],
  entities: readonly ClientEntity[],
  skippedSheets: readonly number[] = [],
): number[] {
  if (entities.some((e) => e.isDefault)) return [];
  if (selectableEntities(entities).length <= 1) return [];
  const out: number[] = [];
  columns.forEach((sheet, i) => {
    if (
      !skippedSheets.includes(i) &&
      sheet.some((c) => c.kind === "year" && c.year !== undefined) &&
      !sheet.some((c) => c.kind === "entity")
    )
      out.push(i);
  });
  return out;
}

/** Geriye uyumlu boolean: en az bir sayfa için işletme seçimi gerekiyor mu. */
export function needsDefaultEntity(
  columns: readonly (readonly KpiImportColumn[])[],
  entities: readonly ClientEntity[],
  skippedSheets: readonly number[] = [],
): boolean {
  return sheetsNeedingEntity(columns, entities, skippedSheets).length > 0;
}

/**
 * K14 (Finar 7.2) — sayfa adı grup toplamı gibi görünüyor mu. Grup toplamı tek işletmeye
 * yazılmamalı (konsolidasyonu sistem hesaplar) → atlama önerilir (otomatik atlanmaz).
 */
export function looksLikeGroupTotalSheet(name: string): boolean {
  return /toplam|konsolide|grup|holding/.test(name.toLocaleLowerCase("tr"));
}

/**
 * Atlanmamış, adı grup toplamına benzeyen sayfaların indeksleri (öneri satırı). Final review I3:
 * yalnız çok işletmeli müşteride (tek işletmeli THY'nin tek veri sayfası "KONSOLİDE KPI'LAR 2025")
 * ve atlanmamış tek kalan veri sayfası asla önerilmez (atlanırsa aktarılacak bir şey kalmaz).
 */
export function groupTotalSheets(
  grid: KpiImportGrid,
  entities: readonly ClientEntity[],
  skippedSheets: readonly number[] = [],
): number[] {
  if (selectableEntities(entities).length <= 1) return [];
  const open = grid.sheets.map((_, i) => i).filter((i) => !skippedSheets.includes(i));
  if (open.length <= 1) return [];
  return open.filter((i) => looksLikeGroupTotalSheet(grid.sheets[i]!.name));
}

/**
 * Zorunlu seçimlerden eksik olanlar (İleri kilidi). P2-6: yıl, yalnız yıl kolonu olmayan VE
 * atlanmamış sayfalardan etkin yılı (sayfa bazlı ya da global) olmayan biri varsa eksik —
 * tek sayfa yüzünden tüm aktarım kilitlenmez; o sayfa atlanabilir ya da kendi yılı seçilir.
 */
export function missingDefaults(
  columns: readonly (readonly KpiImportColumn[])[],
  entities: readonly ClientEntity[],
  defaults: KpiMappingDefaults,
): ("year" | "entity")[] {
  const out: ("year" | "entity")[] = [];
  const skipped = defaults.skippedSheets ?? [];
  if (
    sheetsNeedingYear(columns, skipped).some((i) => effectiveSheetYear(defaults, i) === undefined)
  )
    out.push("year");
  if (needsDefaultEntity(columns, entities, skipped) && defaults.defaultEntityId === undefined)
    out.push("entity");
  return out;
}

/**
 * K13 — not/açıklama sayfaları (çıkarım `kind: "notes"`) ve şu anki durumları: ilk taslakta
 * sunucu bunları atlanmış olarak yazar; kullanıcı geri açarsa `skipped:false` olur.
 */
export function notesSheetStates(
  grid: KpiImportGrid,
  skippedSheets: readonly number[] = [],
): { idx: number; name: string; skipped: boolean }[] {
  return notesSheetIndexes(grid).map((idx) => ({
    idx,
    name: grid.sheets[idx]!.name,
    skipped: skippedSheets.includes(idx),
  }));
}

/** K13 — yan yana çok bloklu sayfalar (blok sayısıyla; atlananlar hariç). */
export function blockSheets(
  grid: KpiImportGrid,
  skippedSheets: readonly number[] = [],
): { idx: number; name: string; blocks: number }[] {
  return grid.sheets.flatMap((s, idx) =>
    s.blockCount !== undefined && !skippedSheets.includes(idx)
      ? [{ idx, name: s.name, blocks: s.blockCount }]
      : [],
  );
}
